<template>
  <div>
    <v-combobox
      v-model="selectedItems"
      :label="label"
      :items="items"
      item-title="text"
      item-value="id"
      :placeholder="placeholder"
      :disabled="disabled"
      :multiple="multiple"
      clearable
      variant="outlined"
      density="compact"
      color="fieldOutlineColor"
      @update:model-value="setNewValue"
      @blur="validateSelection"
    />
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, onMounted, ref } from "vue";
import type { SelectItem } from "@/models/selectItem";
import type { Ref } from "vue";

const props = defineProps<{
  items: SelectItem[];
  selectValues: string[];
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  multiple?: boolean;
}>();

const emit = defineEmits<{
  (e: "fieldUpdated", newValues: string[]): void;
}>();

const selectedItems: Ref<SelectItem[] | string> = ref([]);
onBeforeMount(() => {
  selectedItems.value = getItemsForValues(
    (props.selectValues as string[]) ?? new Array<string>()
  );
});

onMounted(() => {
  emit("fieldUpdated", getValuesForItems(selectedItems.value));
});

function emitNewValue(): void {
  emit("fieldUpdated", getValuesForItems(selectedItems.value));
}

function setNewValue(newItems: any): void {
  selectedItems.value = newItems ?? [];
  emitNewValue();
}

function validateSelection(): void {
  if (typeof selectedItems.value === "string") {
    const item = props.items.find(
      (x: SelectItem) =>
        x.text.toLocaleLowerCase() ==
        (selectedItems.value as string).toLocaleLowerCase()
    );
    selectedItems.value = item === undefined ? new Array<SelectItem>() : [item];
  } else {
    selectedItems.value = selectedItems.value.filter(
      (item: SelectItem) =>
        props.items.findIndex((x: SelectItem) => x.id === item.id) !== -1
    );
  }
  emitNewValue();
}

function getValuesForItems(
  searchItems: SelectItem[] | SelectItem | string
): string[] {
  if (typeof searchItems === "string") {
    return [];
  }
  const searchable = getSearchable<SelectItem>(searchItems);
  const _values = new Array<string>();
  searchable.forEach((searchItem: SelectItem) => {
    const item = getItemForId(searchItem.id);
    if (item !== undefined) {
      _values.push(item.id);
    }
  });
  return _values;
}

function getItemsForValues(searchValues: string[]): SelectItem[] {
  const searchable = getSearchable<string>(searchValues);
  const _items = new Array<SelectItem>();
  searchable.forEach((searchValue: string) => {
    const item = getItemForId(searchValue);
    if (item !== undefined) {
      _items.push(item);
    }
  });

  return _items;
}

function getSearchable<T>(input: T | T[]): T[] {
  return Array.isArray(input) ? (input as T[]) : new Array<T>(input);
}

function getItemForId(id: string): SelectItem | undefined {
  return props.items.find(
    (item: SelectItem) => item.id.toLocaleLowerCase() === id.toLocaleLowerCase()
  );
}
</script>
